// Product Library — saved globally, reused across events. localStorage only.
const PRODUCTS_KEY = "popupprofit:products";

const read = (k, f) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : f; } catch { return f; }
};
const write = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ }
};
const num = (v) => (Number.isFinite(+v) ? +v : 0);

// ---------- CRUD ----------
export function getProducts() {
  return read(PRODUCTS_KEY, []);
}

export function saveProduct(product) {
  const list = getProducts();
  const next = { ...product };
  if (next.id) {
    const idx = list.findIndex((p) => p.id === next.id);
    if (idx >= 0) list[idx] = next;
    else list.push(next);
  } else {
    next.id = `prod_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    next.createdAt = new Date().toISOString();
    list.push(next);
  }
  write(PRODUCTS_KEY, list);
  return next;
}

export function deleteProduct(id) {
  write(PRODUCTS_KEY, getProducts().filter((p) => p.id !== id));
}

export function getProduct(id) {
  return getProducts().find((p) => p.id === id) || null;
}

// ---------- Per-event product math ----------
export function computeProductLine(eventProduct, product) {
  if (!product) {
    return { revenue: 0, cost: 0, profit: 0, margin: 0, unsold: 0, salePrice: 0, costPerItem: 0, qtyBrought: 0, qtySold: 0 };
  }
  const qtyBrought = num(eventProduct?.qtyBrought);
  const qtySold = Math.min(num(eventProduct?.qtySold), qtyBrought || Number.POSITIVE_INFINITY);
  const salePrice = num(eventProduct?.salePriceOverride) || num(product.defaultSalePrice);
  const costPerItem = num(product.costPerItem);
  const revenue = qtySold * salePrice;
  const cost = qtyBrought * costPerItem;
  const profit = revenue - cost;
  const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
  const unsold = Math.max(0, qtyBrought - qtySold);
  return { revenue, cost, profit, margin, unsold, salePrice, costPerItem, qtyBrought, qtySold };
}

export function getEventProductSummary(event) {
  const eventProducts = event?.eventProducts || [];
  const productsLib = getProducts();
  const lookup = {};
  productsLib.forEach((p) => { lookup[p.id] = p; });

  let totalRevenue = 0;
  let totalCost = 0;
  let totalUnsold = 0;
  let bestSeller = null;
  let highestMargin = null;
  let highestProfit = null;
  const lines = [];

  eventProducts.forEach((ep) => {
    const product = lookup[ep.productId] || null;
    const line = computeProductLine(ep, product);
    lines.push({ eventProduct: ep, product, line });
    totalRevenue += line.revenue;
    totalCost += line.cost;
    totalUnsold += line.unsold;
    if (product && line.qtySold > 0) {
      if (!bestSeller || line.qtySold > bestSeller.qtySold) {
        bestSeller = { product, qtySold: line.qtySold };
      }
      if (line.margin > 0 && (!highestMargin || line.margin > highestMargin.margin)) {
        highestMargin = { product, margin: line.margin };
      }
      if (!highestProfit || line.profit > highestProfit.profit) {
        highestProfit = { product, profit: line.profit };
      }
    }
  });

  return {
    lines,
    totalRevenue,
    totalCost,
    totalProfit: totalRevenue - totalCost,
    totalUnsold,
    bestSeller,
    highestMargin,
    highestProfit,
    count: eventProducts.length,
  };
}

// Library-level summary used by the Dashboard card
export function getProductsLibrarySummary() {
  const products = getProducts();
  let catalogValue = 0;
  let bestMarginProduct = null;
  products.forEach((p) => {
    const price = num(p.defaultSalePrice);
    const cost = num(p.costPerItem);
    catalogValue += price;
    const margin = price > 0 ? ((price - cost) / price) * 100 : 0;
    if (price > 0 && (!bestMarginProduct || margin > bestMarginProduct.margin)) {
      bestMarginProduct = { product: p, margin };
    }
  });
  return {
    count: products.length,
    catalogValue,
    bestMarginProduct,
  };
}

// Sample previews shown to free users on the Products page
export const SAMPLE_PRODUCTS = [
  { id: "sample-1", name: "Lavender Soap", category: "Soap", costPerItem: 3.5, defaultSalePrice: 8, notes: "Bestseller in summer" },
  { id: "sample-2", name: "Beeswax Candle", category: "Candles", costPerItem: 4, defaultSalePrice: 12, notes: "4 oz tin" },
  { id: "sample-3", name: "Local Honey 8oz", category: "Pantry", costPerItem: 6, defaultSalePrice: 15, notes: "Raw, wildflower" },
];
